import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import * as TimelineService from "_services/timeline-service";
import { formatFLightFeedByHour, formatFlightListFromBookings, getDate, getHalfHour, getIntervalIndex, groupAndFormatALTPickupTasks, mergeFlightIntervals, processFlightsByInterval } from '_utils/functions';

export const getFlights = createAsyncThunk("flightInformationList/getFlights", async ({ iata, date }) => {
  return TimelineService.getFlightInformationList({ iata, date });
}
);

export const getOutboundFlights = createAsyncThunk("flightInformationList/getOutboundFlights", async ({ iata, date }) => {
  return TimelineService.getOutboundFlights({ iata, date });
}
);

export const getInboundFlights = createAsyncThunk("flightInformationList/getInboundFlights", async ({ iata, date }) => {
  return TimelineService.getInboundFlights({ iata, date });
}
);

export const getPassengerFlightList = createAsyncThunk("flightInformationList/getPassengerFlightList", async (params) => {
  return TimelineService.getPassengerFlightList(params);
}
);

export const createFlightConversation = createAsyncThunk("flightInformationList/createFlightConversation", async (params) => {
  return TimelineService.createFlightConversation(params);
}
);


export const sendFlightMessage = createAsyncThunk("flightInformationList/sendFlightMessage", async (params) => {
  return TimelineService.sendFlightMessage(params);
}
);

export const setFlightMessagesToSeen = createAsyncThunk(
  "flightInformationList/setFlightMessagesToSeen",
  async (props) => {
    return TimelineService.setFlightMessagesToSeen(props);
  }
);

export const getFlightConversation = createAsyncThunk("flightInformationList/getFlightConversation", async (convId) => {
  return TimelineService.getFlightConversation(convId);
}
);

const flightInformationListSlice = createSlice({
  name: 'flightInformationList',
  initialState: {
    response: null,
    flightInformationList: [],
    flightList: [],

    inboundFlights: [],
    outboundFlights: [],

    tableType: 'departures',
    status: 'idle',
    error: null,
    queryTime: null,   // the last time we fetched data

    expandedFlights: [],

    passengerFlightList: [],
    getPassengerFlightListStatus: 'idle',
    getPassengerFlightListError: null,

    searchedFlight: "",

    selectedFlightToChat: null,


    sendFlightMessageStatus: 'idle',
    sendFlightMessageError: null,

    flightMessages: [],
  },
  reducers: {
    reset: (state) => {
      state.response = null;
      state.flightInformationList = [];
      state.tableType = 'departures';
      state.status = 'idle';
      state.error = null;
    },
    setTableType: (state, action) => {
      state.tableType = action.payload;
    },
    setQueryTime: (state, action) => {
      state.queryTime = action.payload;
    },
    setExpandedFlights: (state, action) => {
      state.expandedFlights = action.payload;
    },

    resetPassengerFlightList: (state) => {
      state.passengerFlightList = [];
      state.getPassengerFlightListStatus = 'idle';
      state.getPassengerFlightListError = null;
    },

    setSearchedFlight: (state, action) => {
      state.searchedFlight = action.payload;
    },
    setSelectedFlightToChat: (state, action) => {
      state.selectedFlightToChat = action.payload;
    },

    resetSendFlightMessageFlags: (state) => {
      state.sendFlightMessageStatus = 'idle';
      state.sendFlightMessageError = null;
    },

    setFlightMessages: (state, action) => {
      state.flightMessages = action.payload;
    }
  },
  extraReducers: {
    [getFlights.pending]: (state, action) => {
      state.status = "loading";
    },
    [getFlights.fulfilled]: (state, action) => {
      if (action.payload && action.payload.flights) {
        state.status = "succeeded";
        state.response = action.payload;
        state.flightInformationList = action.payload.flights;

        let flights = Object.keys(action.payload.flights);
        state.flightList = flights;

        state.queryTime = new Date();                   // cache query time
      }
    },
    [getFlights.rejected]: (state, action) => {
      state.status = "failed";
      state.flightInformationList = [];
      state.error = action.error.message;
    },
    [getOutboundFlights.pending]: (state, action) => {
      state.status = "loading";
    },
    [getOutboundFlights.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.outboundFlights = action.payload;
    },
    [getOutboundFlights.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.error.message;
      state.outboundFlights = [];
    },
    [getInboundFlights.pending]: (state, action) => {
      state.status = "loading";
    },
    [getInboundFlights.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.inboundFlights = action.payload;
    },
    [getInboundFlights.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.error.message;
      state.inboundFlights = [];
    },

    [getPassengerFlightList.pending]: (state, action) => {
      state.getPassengerFlightListStatus = "loading";
    },
    [getPassengerFlightList.fulfilled]: (state, action) => {
      state.getPassengerFlightListStatus = "succeeded";
      state.passengerFlightList = action.payload;
    },
    [getPassengerFlightList.rejected]: (state, action) => {
      state.getPassengerFlightListStatus = "failed";
      state.getPassengerFlightListError = action.error.message;
    },

    [sendFlightMessage.pending]: (state, action) => {
      state.sendFlightMessageStatus = 'loading';
      state.sendFlightMessageError = null;
    },
    [sendFlightMessage.fulfilled]: (state, action) => {
      state.sendFlightMessageStatus = 'succeeded';
    },
    [sendFlightMessage.rejected]: (state, action) => {
      state.sendFlightMessageStatus = 'failed';
      state.sendFlightMessageError = action.error.message;
    },
  }
});

export default flightInformationListSlice.reducer;

export const selectOutboundFlights = state => {
  if (["JER", "DUB", "ORK", "BFS", "SNN", "PUJ"].includes(state.user.dbInfo.details.authority)) {
    return state.flightInformationList.outboundFlights;
  } else {
    return formatFlightListFromBookings(state.timelineData.departureBookings, state.user.dbInfo.details.authority, getDate(state.timelineControl.timelineDate));
  }
};
export const selectInboundFlights = state => {
  if (["JER", "DUB", "ORK", "BFS", "SNN", "PUJ"].includes(state.user.dbInfo.details.authority)) {
    return state.flightInformationList.inboundFlights;
  } else {
    return formatFlightListFromBookings(state.timelineData.bookings, state.user.dbInfo.details.authority, getDate(state.timelineControl.timelineDate));
  }
};

export const selectALTPickUpFlights = state => {
  let ALTPickUpTaskList = Array.isFullArray(state.timelineData.taskList) ? state.timelineData.taskList?.filter((element) => element?.isALTPickUp == true && element?.pickUpTime) : [];
  if (Array.isFullArray(ALTPickUpTaskList)) {

    const groupedALTPickUpFlights = groupAndFormatALTPickupTasks(ALTPickUpTaskList);
    return groupedALTPickUpFlights;
  }
  return [];
};

export const selectFlightListIndexed = createSelector(
  [
    state => state.flightInformationList.outboundFlights,
    state => state.flightInformationList.inboundFlights,
  ],
  (outboundFlights, inboundFlights) => {
    let map = {};

    // map each outbound flight into the map dictionary
    if (outboundFlights && Array.isArray(outboundFlights) && outboundFlights.length > 0) {
      outboundFlights.forEach((outboundFlight) => {
        let flightNumber = outboundFlight.flightNumber;
        map[`${flightNumber}_${outboundFlight?.departureAirport}_${outboundFlight?.arrivalAirport}`] = outboundFlight;
      });
    }

    // map each inbound flight too
    if (inboundFlights && Array.isArray(inboundFlights) && inboundFlights.length > 0) {
      inboundFlights.forEach((inboundFlight) => {
        let flightNumber = inboundFlight.flightNumber;
        map[`${flightNumber}_${inboundFlight?.departureAirport}_${inboundFlight?.arrivalAirport}`] = inboundFlight;
      });
    }

    return map;
  }
);

export const selectAllFlights = state => state.flightInformationList.flightInformationList;
export const selectFlightStatus = state => state.flightInformationList.status;
export const selectFlightError = state => state.flightInformationList.error;

export const selectTableType = state => state.flightInformationList.tableType;

export const selectQueryTime = state => state.flightInformationList.queryTime;

export const selectResponse = state => state.flightInformationList.response;

export const selectExpandedFlights = state => state.flightInformationList.expandedFlights;

export const selectPassengerFlightList = state => state.flightInformationList.passengerFlightList;
export const selectGetPassengerFlightListStatus = state => state.flightInformationList.getPassengerFlightListStatus;
export const selectSearchedFlight = state => state.flightInformationList.searchedFlight;
export const selectSelectedFlightToChat = state => state.flightInformationList.selectedFlightToChat;
export const selectSendFlightMessageStatus = state => state.flightInformationList.sendFlightMessageStatus;
export const selectSendFlightMessageError = state => state.flightInformationList.sendFlightMessageError;

export const selectFlightList = state => {
  if (state.flightInformationList.flightInformationList === null || state.flightInformationList.flightInformationList === undefined) {
    return [];
  }

  // return list of flights
  let flightKeys = Object.keys(state.flightInformationList.flightInformationList);

  return [];
}

// list for tineline table
export const selectFlightTableList = state => {

  // check if null or undefined or empty
  if (state.flightInformationList.flightInformationList === null || state.flightInformationList.flightInformationList === undefined) {
    return [];
  }

  //console.log('flight info list', state.flightInformationList.flightInformationList);

  return [];

}

export const selectFlightsByHour = state => {
  let flightNumbersByHour = {
    "00:00": [],
    "01:00": [],
    "02:00": [],
    "03:00": [],
    "04:00": [],
    "05:00": [],
    "06:00": [],
    "07:00": [],
    "08:00": [],
    "09:00": [],
    "10:00": [],
    "11:00": [],
    "12:00": [],
    "13:00": [],
    "14:00": [],
    "15:00": [],
    "16:00": [],
    "17:00": [],
    "18:00": [],
    "19:00": [],
    "20:00": [],
    "21:00": [],
    "22:00": [],
    "23:00": [],
  };
  if (state.timelineControl.thirtyMinLayout) {
    flightNumbersByHour = {}
    // Initialize with empty objects for each 30-minute interval
    Array.from({ length: 48 }, (_, index) => {
      const hour = Math.floor(index / 2);
      const minutes = index % 2 === 0 ? "00" : "30";
      const time = `${hour.toString().padStart(2, "0")}:${minutes}`;
      flightNumbersByHour[time] = []; // Initialize as an empty array
    });
  }

  let iata = state.user.dbInfo.details.authority
  if (["PUJ", "DUB", "BFS", "JER"].includes(iata)) {
    return flightNumbersByHour;
  }
  // if (!Array.isFullArray(state.flightInformationList.outboundFlights) && !Array.isFullArray(state.flightInformationList.inboundFlights) && ["JER", "DUB", "ORK", "BFS", "SNN", "PUJ"].includes(iata)) {
  //   //console.log('inbound flight or outbound flights are empty, returning empty list')
  //   return flightNumbersByHour;
  // }
  let arrivalFlights = Array.isFullArray(state.flightInformationList.inboundFlights) ? [...state.flightInformationList.inboundFlights] : [];
  let departureFlights = Array.isFullArray(state.flightInformationList.outboundFlights) ? [...state.flightInformationList.outboundFlights] : [];
  let departureBooking = Array.isFullArray(state?.timelineData?.departureBookings) ? state?.timelineData?.departureBookings : [];
  let arrivalBooking = Array.isFullArray(state?.timelineData?.bookings) ? state?.timelineData?.bookings : [];
  let departureBookingFlights = [];
  let arrivalBookingFlights = [];
  // console.log(departureBooking, "departureBooking");

  if (!["JER", "DUB", "ORK", "BFS", "SNN", "PUJ", "EDI", "SNN"].includes(iata) || (!Array.isFullArray(arrivalFlights) && !Array.isFullArray(departureFlights))) {
    // Format flights from bookings (pure conversion)
    const arrivalFlightsFromBookings = formatFlightListFromBookings(arrivalBooking, iata, getDate(state.timelineControl.timelineDate));
    const departureFlightsFromBookings = formatFlightListFromBookings(departureBooking, iata, getDate(state.timelineControl.timelineDate));
    // console.log(arrivalFlightsFromBookings, "arrivalFlightsFromBookings");
    // console.log(departureFlightsFromBookings, "departureFlightsFromBookings");

    // Merge and add to timeline
    arrivalFlights = [...arrivalFlightsFromBookings];
    departureFlights = [...departureFlightsFromBookings];
  }

  // alternative pick up tasks list
  let ALTPickUpTaskList = Array.isFullArray(state.timelineData.taskList) ? state.timelineData.taskList?.filter((element) => element?.isALTPickUp == true) : [];
  let ALTPickUpFlights = [];
  let tempALTPickUpFlights = iata !== "JER" && iata !== "SNN"
    ? [...new Set(ALTPickUpTaskList?.filter((item) => !item?.pickUpTime)?.map((item) => item?.flightInformation?.flightNumber))]
    : [...new Set(ALTPickUpTaskList?.map((item) => item?.flightInformation?.flightNumber))]
  ALTPickUpFlights = departureFlights?.filter((flight) => tempALTPickUpFlights.includes(flight?.flightNumber));
  let groupedALTPickUpFlights = iata !== "JER" && iata !== "SNN"
    ? groupAndFormatALTPickupTasks(Array.isFullArray(state.timelineData.taskList) ? state.timelineData.taskList?.filter((element) => element?.isALTPickUp == true && element?.pickUpTime) : [])
    : [];

  // check if show All Flights switch is switched off
  if (state.timelineControl.showAllFlights === false) {
    // check if there is an departure booking process departureFlights else make it an empty array
    if (Array.isFullArray(departureBooking)) {
      // set departureBookingFlights to an array of the departureBooking flights numbers
      departureBookingFlights = [...new Set(departureBooking?.map((item) => item?.doc?.flightConfirmationDetails?.carrierCode + item?.doc?.flightConfirmationDetails?.flightNumber))];
      // check if departureBookingFlights is an array and has length > 0 filter the departureFlights to only includes the flights in the departureBookingFlights
      if (Array.isFullArray(departureBookingFlights)) {
        departureFlights = departureFlights?.filter((flight) => departureBookingFlights.includes(flight?.flightNumber))
      }
    } else {
      departureFlights = [];
    };
    // check if there is an arrival booking process arrivalFlights else make it an empty array
    if (Array.isFullArray(arrivalBooking)) {
      // set arrivalBookingFlights to an array of the arrivalBooking flights numbers
      arrivalBookingFlights = [...new Set(arrivalBooking?.map((item) => item?.doc?.flightConfirmationDetails?.carrierCode + item?.doc?.flightConfirmationDetails?.flightNumber))];
      // check if arrivalBookingFlights is an array an has length > 0 filter the arrivalFlights to only includes the flights in the arrivalBookingFlights
      if (Array.isFullArray(arrivalBookingFlights)) {
        arrivalFlights = arrivalFlights?.filter((flight) => arrivalBookingFlights.includes(flight?.flightNumber))
      }
    } else {
      arrivalFlights = [];
    }
  }

  let tempDepartures = [...departureFlights];
  let checkedInBookings = [];

  let locationList = [];
  //#region filtering flights

  // check if the selected bookings filter doesn't includes all or departures or boarding make the departureFlights to empty array
  if (!state?.timelineAreas?.bookingType?.some(type => ["all", "departures", "boarding", "checkedIn"].includes(type))) {
    tempDepartures = [];
  };
  if (!state?.timelineAreas?.bookingType?.some(type => ["all", "departures", "boarding", "checkedIn"].includes(type)) && state?.timelineAreas?.bookingType?.some(type => ["ALTPickUp"].includes(type)) && Array.isFullArray(ALTPickUpFlights)) {
    tempDepartures = departureFlights?.filter((flight) => ALTPickUpFlights?.map((flight) => flight?.flightNumber).includes(flight?.flightNumber));
  };
  // check if the selected bookings filter doesn't includes all or arrivals or ramp make the arrivalFlights to empty array
  if (!state?.timelineAreas?.bookingType?.some(type => ["all", "arrivals", "ramp"].includes(type))) {
    arrivalFlights = [];
  }

  departureBooking.forEach((booking) => {
    if (booking && booking.doc != undefined && booking.doc.paxPositionInfo && booking.doc.paxPositionInfo.activity && booking.doc.paxPositionInfo.activity == 'Checked-in') {
      checkedInBookings.push(booking);
    }
  });


  // check if the selected bookings filter doesn't include all or arrivals make the arrivalFlights empty array

  //#endregion

  // check if a specific timeline is selected
  if (state.timelineAreas.selectedTimeline !== null) {
    // a timeline has been selected, we need to filter the flights by the selected timeline using the gates / stands 
    // that is stored in the selected timeline location list
    // need to filter airport location lists by the selected timeline
    let selectedTimelineLocationsTemp = state.timelineAreas.selectedTimeline.location;

    if (selectedTimelineLocationsTemp != undefined && selectedTimelineLocationsTemp.length > 0) {

      selectedTimelineLocationsTemp.forEach((location) => {
        let fullLocationObject = state.timelineAreas.airportLocations.find((airportLocation) => airportLocation._id === location.locationId);
        if (fullLocationObject != undefined) {
          if (fullLocationObject.gate != undefined && fullLocationObject.gate != '') {
            locationList.push(`GATE:${fullLocationObject.gate}`);
          }
          if (fullLocationObject.stand != undefined && fullLocationObject.stand != '') {
            locationList.push(`STAND:${fullLocationObject.stand}`);
          }
        }
      });
    }

  };

  let flightsArray = [];
  if (Array.isFullArray(tempDepartures) || Array.isFullArray(arrivalFlights)) {

    let arrayToSort = [];

    if (Array.isFullArray(tempDepartures)) arrayToSort = [...tempDepartures];
    if (Array.isFullArray(arrivalFlights)) arrayToSort = [...arrayToSort, ...arrivalFlights];

    // sorting departures by time 
    const sortedFlights = [...arrayToSort].sort((a, b) => {
      const timeA = new Date(a?.operationInfo?.time).getTime();
      const timeB = new Date(b?.operationInfo?.time).getTime();
      // negative value if a is older than b (so a comes first), positive value if a is newer than b (so b comes first)
      return timeA - timeB;
    });
    flightsArray = [...sortedFlights]

  };

  // check if there is no selected area (View ALL is pressed) or the selected area(selectedTimeline) is for departures or arrivals
  if (state.timelineAreas.selectedTimeline?.departures === true || state.timelineAreas.selectedTimeline?.arrivals === true || state.timelineAreas.selectedTimeline === null) {

    // if the selected timeline isn't for departures only or for all (selectedTimeline is null) filter the flights to only include the arrivals only
    if (state.timelineAreas.selectedTimeline?.departures !== true && state.timelineAreas.selectedTimeline !== null) {
      flightsArray = flightsArray?.filter((flight) => flight?.type !== "departure");
    };

    // if the selected timeline isn't for arrivals only or for all (selectedTimeline is null) filter the flights to only include the departures only 
    if (state.timelineAreas.selectedTimeline?.arrivals !== true && state.timelineAreas.selectedTimeline !== null) {
      flightsArray = flightsArray?.filter((flight) => flight?.type !== "arrival");
    };
    // need to add each flight in inbound + outbound flights to the flight numbers by hour
    if (flightsArray.length === 0) { } else {
      formatFLightFeedByHour(state.timelineControl.thirtyMinLayout,
        flightsArray, locationList, flightNumbersByHour,
        state.timelineControl.preBoard, false,
        state?.flightInformationList?.searchedFlight,
        state?.timelineAreas?.bookingType,
        checkedInBookings,
        iata == "JER" || iata == "SNN" ? ALTPickUpFlights?.map((flight) => flight?.flightNumber) : []);
    }

  };

  // check if ALTPickUpTaskList is an array with length and make it an array of flightNumbers only 
  if (iata !== "JER" && iata !== "SNN" && Array.isFullArray(ALTPickUpTaskList) && Array.isFullArray(departureFlights) && (state.timelineAreas.bookingType?.includes("all") || state.timelineAreas.bookingType?.includes("ALTPickUp"))) {

    // need to add each flight in inbound + outbound flights to the flight numbers by hour

    if (Array.isFullArray(ALTPickUpFlights)) formatFLightFeedByHour(state.timelineControl.thirtyMinLayout, ALTPickUpFlights, locationList, flightNumbersByHour, false, true, state?.flightInformationList?.searchedFlight);
    if (Array.isFullArray(groupedALTPickUpFlights)) formatFLightFeedByHour(state.timelineControl.thirtyMinLayout, groupedALTPickUpFlights, locationList, flightNumbersByHour, false, true, state?.flightInformationList?.searchedFlight);

  }

  // go through each hour and remove any duplicates
  Object.keys(flightNumbersByHour).forEach((hour) => {
    flightNumbersByHour[hour] = [...new Set(flightNumbersByHour[hour])];
  });

  // Sort each hour's array: /ALTPickUp items first, then others
  Object.keys(flightNumbersByHour).forEach((hour) => {
    flightNumbersByHour[hour] = flightNumbersByHour[hour].sort((a, b) => {
      const aHasAltPickup = a.includes('/ALTPickUp');
      const bHasAltPickup = b.includes('/ALTPickUp');

      // If both have /ALTPickUp or both don't have it, maintain original order
      if (aHasAltPickup === bHasAltPickup) return 0;

      // If only a has /ALTPickUp, it should come first (return -1)
      if (aHasAltPickup) return -1;

      // If only b has /ALTPickUp, it should come first (return 1)
      return 1;
    });
  });

  return flightNumbersByHour;
}


export const getLocationList = (state) => {
  let locationList = [];

  // Check if a specific timeline is selected
  if (state.timelineAreas.selectedTimeline !== null) {
    // Filter flights by the selected timeline using gates/stands
    let selectedTimelineLocationsTemp = state.timelineAreas.selectedTimeline.location;

    if (selectedTimelineLocationsTemp !== undefined && selectedTimelineLocationsTemp.length > 0) {
      selectedTimelineLocationsTemp.forEach((location) => {
        let fullLocationObject = state.timelineAreas.airportLocations.find(
          (airportLocation) => airportLocation._id === location.locationId
        );
        if (fullLocationObject !== undefined) {
          if (fullLocationObject.gate !== undefined && fullLocationObject.gate !== '') {
            locationList.push(fullLocationObject.gate);
          }
          if (fullLocationObject.stand !== undefined && fullLocationObject.stand !== '') {
            locationList.push(fullLocationObject.stand);
          }
        }
      });
    }
  }

  return locationList;
};


export const selectFlightMessages = (state) => {
  const processedMessages = [];
  let lastDate = '';
  let messages = Array.isFullArray(state.flightInformationList.flightMessages) ? [...state.flightInformationList.flightMessages] : [];

  [...messages].reverse().forEach((msg) => {

    const msgDate = msg?.sentAt?.seconds ? new Date(msg?.sentAt?.seconds * 1000) : new Date(msg?.sentAt);
    const currentDate = msgDate.toDateString();

    if (currentDate !== lastDate) {
      processedMessages.push({
        type: 'header',
        date: currentDate,
        id: `header-${currentDate}`
      });
      lastDate = currentDate;
    }

    processedMessages.push(msg);
  });
  return processedMessages;
};

export const { reset, setTableType, setQueryTime, setExpandedFlights, resetPassengerFlightList, setSearchedFlight, setSelectedFlightToChat, resetSendFlightMessageFlags, setFlightMessages } = flightInformationListSlice.actions;